import { useEffect, useMemo, useRef } from "react";
import { type RequestClientConfig, requestClient } from "../index";

/**
 * 组件内发请求的入口。返回的 `request` / `cancel` 引用是稳定的，
 * `src/api/*` 里 `useMemo(..., [request])` 才能真正缓存住 API 对象；
 * 在途请求挂在同一个 controller 集合上，`cancel()` 能把它们一起中断。
 *
 * 组件真正卸载时自动 abort。清理延迟到 microtask，并用生命周期序号确认没有紧接着
 * 重新挂载，从而避开 StrictMode 开发期 effect setup → cleanup → setup 对请求的误杀。
 */
export function useRequest() {
  const controllersRef = useRef<Set<AbortController>>(null);
  const lifecycleRef = useRef(0);
  controllersRef.current ??= new Set<AbortController>();

  const requestApi = useMemo(() => {
    const controllers = controllersRef.current as Set<AbortController>;

    function request<T>(url: string, config: RequestClientConfig): Promise<T> {
      const controller = new AbortController();
      const querySignal = config.signal;
      const cancelFromQuery = () => controller.abort();

      if (querySignal?.aborted) {
        controller.abort();
      } else {
        querySignal?.addEventListener?.("abort", cancelFromQuery, { once: true });
      }

      controllers.add(controller);
      return requestClient
        .request<T>(url, {
          ...config,
          signal: controller.signal,
        })
        .finally(() => {
          querySignal?.removeEventListener?.("abort", cancelFromQuery);
          controllers.delete(controller);
        });
    }

    function cancel(message?: string) {
      controllers.forEach((controller) => {
        controller.abort(message);
      });
      controllers.clear();
    }

    return { cancel, request };
  }, []);

  useEffect(() => {
    const lifecycle = ++lifecycleRef.current;

    return () => {
      queueMicrotask(() => {
        if (lifecycleRef.current === lifecycle) {
          requestApi.cancel("组件已卸载");
        }
      });
    };
  }, [requestApi]);

  return requestApi;
}
